import type { NpMessageBlockTarget } from '#graphql/build-get-messages-variables';

export const NP_MESSAGE_TAG = 'np-message';
export const NP_MESSAGE_OPEN_TAG = `<${NP_MESSAGE_TAG}>`;
export const NP_MESSAGE_CLOSE_TAG = `</${NP_MESSAGE_TAG}>`;

export type NpMessageTarget = NpMessageBlockTarget;

export interface MessageBlockData {
  target: NpMessageTarget;
  messageId?: string;
}

export interface ParsedMarkdownMessageConfig {
  data: MessageBlockData;
  rawConfig: string;
  parseError: string | null;
}

export interface MarkdownMessageEditValues {
  target: NpMessageTarget;
  messageId: string;
}

export interface MarkdownMessageEditRequest {
  mode: 'edit' | 'insert';
  initialValues: MarkdownMessageEditValues;
  onCommit: (values: MarkdownMessageEditValues) => void | Promise<void>;
}

const NP_MESSAGE_TARGETS = new Set<NpMessageTarget>([
  'all',
  'raw',
  'document',
  'message',
]);

const TARGET_LABELS: Record<NpMessageTarget, string> = {
  all: 'Chat',
  raw: 'All',
  document: 'document',
  message: 'message',
};

function normalizeRawConfig(value: string): string {
  return value.replace(/\r\n/g, '\n').trim();
}

function minifyJsonConfig(value: unknown): string {
  return JSON.stringify(value);
}

function normalizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function normalizeNpMessageTarget(value: unknown): NpMessageTarget {
  return NP_MESSAGE_TARGETS.has(value as NpMessageTarget)
    ? (value as NpMessageTarget)
    : 'document';
}

export function normalizeMessageBlockData(input: unknown): MessageBlockData {
  const rec =
    input && typeof input === 'object'
      ? (input as Record<string, unknown>)
      : {};

  const target = normalizeNpMessageTarget(rec.target);
  const messageId = normalizeString(rec.messageId);

  if (target === 'message' && messageId) {
    return { target, messageId };
  }

  return { target };
}

function messageDataToSerializableConfig(
  data: MessageBlockData,
): Record<string, string> {
  const normalized = normalizeMessageBlockData(data);
  if (normalized.target === 'message' && normalized.messageId) {
    return {
      target: 'message',
      messageId: normalized.messageId,
    };
  }

  return { target: normalized.target };
}

export function stringifyMarkdownMessageConfig(data: MessageBlockData): string {
  return JSON.stringify(messageDataToSerializableConfig(data), null, 2);
}

export function parseMarkdownMessageConfig(
  rawConfig: string,
): ParsedMarkdownMessageConfig {
  const normalizedRaw = normalizeRawConfig(rawConfig);

  if (!normalizedRaw) {
    return {
      data: normalizeMessageBlockData({}),
      rawConfig: normalizedRaw,
      parseError: null,
    };
  }

  try {
    return {
      data: normalizeMessageBlockData(JSON.parse(normalizedRaw) as unknown),
      rawConfig: normalizedRaw,
      parseError: null,
    };
  } catch (error) {
    return {
      data: normalizeMessageBlockData({}),
      rawConfig: normalizedRaw,
      parseError:
        error instanceof Error ? error.message : 'Could not parse message JSON',
    };
  }
}

export function serializeMarkdownMessageBlock(params: {
  data: MessageBlockData;
  rawConfig?: string;
  parseError?: string | null;
}): string {
  return `${NP_MESSAGE_OPEN_TAG}${serializeMarkdownMessageInnerConfig(params)}${NP_MESSAGE_CLOSE_TAG}`;
}

export function serializeMarkdownMessageInnerConfig(params: {
  data: MessageBlockData;
  rawConfig?: string;
  parseError?: string | null;
}): string {
  return params.parseError && params.rawConfig?.trim()
    ? normalizeRawConfig(params.rawConfig)
    : minifyJsonConfig(messageDataToSerializableConfig(params.data));
}

export function createDefaultMarkdownMessageValue(
  initialValues?: Partial<MarkdownMessageEditValues>,
): string {
  return serializeMarkdownMessageBlock({
    data: normalizeMessageBlockData({
      target: initialValues?.target ?? 'document',
      messageId: initialValues?.messageId,
    }),
  });
}

export function createDefaultMarkdownMessageEditValues(): MarkdownMessageEditValues {
  return {
    target: 'document',
    messageId: '',
  };
}

export function messageBlockDataToEditValues(
  data: MessageBlockData,
): MarkdownMessageEditValues {
  const normalized = normalizeMessageBlockData(data);
  return {
    target: normalized.target,
    messageId: normalized.messageId ?? '',
  };
}

export function markdownMessageEditValuesToBlockData(
  values: MarkdownMessageEditValues,
): MessageBlockData {
  const target = normalizeNpMessageTarget(values.target);
  const messageId = normalizeString(values.messageId);
  if (target === 'message' && messageId) {
    return { target, messageId };
  }

  return { target };
}

export function getMarkdownMessageValidationMessage(
  data: MessageBlockData,
): string | null {
  const normalized = normalizeMessageBlockData(data);
  if (normalized.target === 'message' && !normalized.messageId?.trim()) {
    return 'Message ID is required when target is a single message thread.';
  }

  return null;
}

export function describeMarkdownMessageTarget(target: NpMessageTarget): string {
  return TARGET_LABELS[target] ?? target;
}

export function describeMarkdownMessage(data: MessageBlockData): string {
  const normalized = normalizeMessageBlockData(data);
  const label = describeMarkdownMessageTarget(normalized.target);
  if (normalized.target === 'message' && normalized.messageId) {
    return `Messages · ${label} (${normalized.messageId})`;
  }

  return `Messages · ${label}`;
}
